import React, { useState, useContext } from "react";
import usePageTitle from "../../services/usePageTitle";
import usePageState from "../../hooks/usePageState";
import PageLayout from "../../components/PageLayout";
import SearchToolbar from "../../components/SearchToolbar";
import HiveTable from "./HiveTable";
import {
  CreateTableDialog,
  UploadJsonDialog,
  DatabaseDetailsDialog,
} from "./HiveDialogs";
import {
  fetchHiveTables,
  createHiveTable,
  deleteHiveTable,
  createHiveTableFromJson,
} from "./HiveAPI";
import { AuthContext } from "../../services/authcontext";
import hiveHelp from "../../content/help/hive.json";

export default function Hive({ pageTitle = "Hive Tables" }) {
  usePageTitle("Hive Tables - SkeyeGate™");
  const { user } = useContext(AuthContext);
  const isRoleUser =
    user?.roles?.some((role) => role.name === "ROLE_USER") ?? false;

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchHiveTables,
    filterFields: ["name"],
  });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [databaseDetailsOpen, setDatabaseDetailsOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);

  const handleCreateTable = async (values) => {
    const payload = {
      tableName: values.tableName,
      columnDefinitions: values.columns,
      hdfsLocation: values.hdfsLocation,
      format: values.format,
      hasHeader:
        values.format === "DELIMITED" ? values.hasHeader || false : false,
    };
    if (values.format === "DELIMITED") payload.delimiter = values.delimiter;
    try {
      await createHiveTable(payload);
      messageApi.success("Table created successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleUploadJson = async (file) => {
    try {
      await createHiveTableFromJson(file);
      messageApi.success("Table created successfully from JSON");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteTable = async (tableName) => {
    try {
      await deleteHiveTable(tableName);
      messageApi.success("Table deleted successfully");
      loadData();
    } catch (error) {
      messageApi.error({
        content:
          error.response?.data?.error ||
          error.response?.data?.exception ||
          error.response?.data?.message ||
          error.message ||
          "Failed to delete table",
        key: "delete-table-error",
      });
    }
  };

  return (
    <div className="hive-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logoSrc="/img/hive.svg"
        helpContent={hiveHelp}
        description="Manage your Hive tables, view details, and perform actions like create, delete, and upload from JSON."
      >
        <SearchToolbar
          search={searchText}
          onSearchChange={handleSearch}
          onClearFilter={clearFilter}
          showAdd={!isRoleUser}
          dropdownAdd={
            !isRoleUser
              ? {
                  items: [
                    {
                      key: "1",
                      label: "Create Manually",
                      onClick: () => setCreateModalOpen(true),
                    },
                    {
                      key: "2",
                      label: "Upload JSON File",
                      onClick: () => setUploadModalOpen(true),
                    },
                  ],
                  label: "Add Table Options",
                }
              : undefined
          }
        />
        <HiveTable
          data={filteredData}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onViewTable={(table) => {
            setSelectedTable(table);
            setDatabaseDetailsOpen(true);
          }}
          onDeleteTable={handleDeleteTable}
          isRoleUser={isRoleUser}
        />
      </PageLayout>
      <CreateTableDialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreateTable}
      />
      <UploadJsonDialog
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSave={handleUploadJson}
      />
      <DatabaseDetailsDialog
        open={databaseDetailsOpen}
        onClose={() => setDatabaseDetailsOpen(false)}
        tableName={selectedTable?.name}
      />
    </div>
  );
}
